import React, { useState, useRef, useEffect } from "react";
import { FiFilter, FiX } from "react-icons/fi";
import {
  FilterButton,
  FilterDropdown,
  FilterTypeSelect,
  DateInput,
  FilterActions,
  FilterButtonStyled,
  FilterLabel,
  FilterContainer,
} from "../styles/FilterStyles";

const DateFilter = ({ onFilterChange, fieldName, currentFilter, isDarkMode }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [filterType, setFilterType] = useState(currentFilter?.type || "before");
  const [date1, setDate1] = useState(currentFilter?.value1 || "");
  const [date2, setDate2] = useState(currentFilter?.value2 || "");
  const [alignRight, setAlignRight] = useState(false);
  const [dropdownPosition, setDropdownPosition] = useState({
    top: 0,
    left: 0,
    right: 0,
  });
  const [positionReady, setPositionReady] = useState(false);
  const containerRef = useRef(null);
  const dropdownRef = useRef(null);
  const scrollableParentsRef = useRef([]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target) &&
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target)
      ) {
        setIsOpen(false);
      }
    };

    const handleScroll = () => {
      if (isOpen) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      setPositionReady(false);

      document.addEventListener("mousedown", handleClickOutside);
      window.addEventListener("scroll", handleScroll, true);

      scrollableParentsRef.current = [];
      let parent = containerRef.current?.parentElement;
      while (parent && parent !== document.body) {
        const style = window.getComputedStyle(parent);
        const overflow = style.overflow;
        const overflowY = style.overflowY;
        if (
          overflow === "auto" ||
          overflow === "scroll" ||
          overflowY === "auto" ||
          overflowY === "scroll"
        ) {
          scrollableParentsRef.current.push(parent);
          parent.addEventListener("scroll", handleScroll, true);
        }
        parent = parent.parentElement;
      }

      setTimeout(() => {
        if (containerRef.current) {
          const containerRect = containerRef.current.getBoundingClientRect();
          const dropdownWidth = 320;
          const viewportWidth = window.innerWidth;
          const spaceOnRight = viewportWidth - containerRect.right;
          const spaceOnLeft = containerRect.left;

          const top = containerRect.bottom + 4;
          let shouldAlignRight = false;
          let left = 0;
          let right = 0;

          if (fieldName === "streetViewCompletionDate") {
            const minMargin = 16;
            const maxRight = viewportWidth - minMargin;

            const calculatedRight = viewportWidth - containerRect.right;
            const dropdownRightIfAlignedRight =
              containerRect.right + dropdownWidth;

            if (dropdownRightIfAlignedRight > maxRight) {
              shouldAlignRight = false;
              let calculatedLeft = containerRect.right - dropdownWidth;

              if (calculatedLeft + dropdownWidth > maxRight) {
                calculatedLeft = maxRight - dropdownWidth;
              }

              calculatedLeft = Math.max(minMargin, calculatedLeft);
              left = calculatedLeft;
            } else {
              shouldAlignRight = true;
              right = calculatedRight;
            }
          } else if (
            spaceOnRight < dropdownWidth &&
            spaceOnLeft > dropdownWidth
          ) {
            shouldAlignRight = true;
            right = viewportWidth - containerRect.right;
          } else {
            left = containerRect.left;
          }

          setAlignRight(shouldAlignRight);
          setDropdownPosition({
            top: typeof top === "number" && !isNaN(top) ? top : 0,
            left: typeof left === "number" && !isNaN(left) ? left : 0,
            right: typeof right === "number" && !isNaN(right) ? right : 0,
          });
          setPositionReady(true);
        } else {
          setDropdownPosition({
            top: 100,
            left: 100,
            right: 0,
          });
          setPositionReady(true);
        }
      }, 0);
    } else {
      setPositionReady(false);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      window.removeEventListener("scroll", handleScroll, true);
      scrollableParentsRef.current.forEach((parent) => {
        parent.removeEventListener("scroll", handleScroll, true);
      });
      scrollableParentsRef.current = [];
    };
  }, [isOpen, fieldName]);

  const handleApply = () => {
    const filter = {
      type: filterType,
      value1: date1,
      value2: filterType === "between" ? date2 : undefined,
    };

    onFilterChange(fieldName, filter);
    setIsOpen(false);
  };

  const handleClear = () => {
    setFilterType("before");
    setDate1("");
    setDate2("");
    onFilterChange(fieldName, null);
    setIsOpen(false);
  };

  const hasActiveFilter = currentFilter && currentFilter.value1;

  return (
    <FilterContainer ref={containerRef}>
      <FilterButton
        active={hasActiveFilter}
        onClick={() => setIsOpen(!isOpen)}
        title="Filter by date"
      >
        <FiFilter />
        {hasActiveFilter && <FiX size={12} />}
      </FilterButton>

      {isOpen && positionReady && (
        <FilterDropdown
          ref={dropdownRef}
          alignRight={alignRight}
          top={dropdownPosition.top}
          left={dropdownPosition.left}
          right={dropdownPosition.right}
          fieldName={fieldName}
          isDarkMode={isDarkMode}
        >
          <FilterLabel isDarkMode={isDarkMode}>Filter Type</FilterLabel>
          <FilterTypeSelect
            value={filterType}
            onChange={(e) => setFilterType(e.target.value)}
            isDarkMode={isDarkMode}
          >
            <option value="before">Before than</option>
            <option value="after">After</option>
            <option value="between">Between</option>
          </FilterTypeSelect>

          <FilterLabel isDarkMode={isDarkMode}>
            {filterType === "between" ? "Start Date" : "Date"}
          </FilterLabel>
          <DateInput
            type="date"
            value={date1}
            onChange={(e) => setDate1(e.target.value)}
            isDarkMode={isDarkMode}
          />

          {filterType === "between" && (
            <>
              <FilterLabel isDarkMode={isDarkMode}>End Date</FilterLabel>
              <DateInput
                type="date"
                value={date2}
                onChange={(e) => setDate2(e.target.value)}
                min={date1}
                isDarkMode={isDarkMode}
              />
            </>
          )}

          <FilterActions>
            <FilterButtonStyled className="clear" onClick={handleClear} isDarkMode={isDarkMode}>
              Clear
            </FilterButtonStyled>
            <FilterButtonStyled
              className="apply"
              onClick={handleApply}
              disabled={!date1}
              isDarkMode={isDarkMode}
            >
              Apply
            </FilterButtonStyled>
          </FilterActions>
        </FilterDropdown>
      )}
    </FilterContainer>
  );
};

export default DateFilter;
